"use client";
import { useState, useEffect } from "react";
import { Card, CardContent } from "../UI/card";
import { Button } from "../UI/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../UI/dialog";
import {
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";
import JiraSidebar from "./JiraSidebar";

interface Member {
  id: string;
  name: string;
  email: string;
  role: string;
  dept: string;
  projects: string | number;
  rate: string;
  status: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function TeamManagementPage() {

  const [members, setMembers] = useState<Member[]>([]);


  const [open, setOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "Developer",
    dept: "Engineering",
  });

  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    email: "",
    role: "Developer",
    dept: "Engineering",
  });

  const [users, setUsers] = useState<any[]>([]);

  const fetchMembers = async () => {
    const res = await fetch(`${API_BASE}/get_members.php`);
    const data = await res.json();
    setMembers(data);
  };

  const fetchAllUsers = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/users_list.php`
      );
      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setUsers(data.data);
      }
    } catch (err) {
      console.log("Error loading users", err);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchAllUsers();
  }, []);


  const handleAddMember = async () => {
    if (!form.name || !form.email) return;

    // Check if user exists in database list
    const userExists = users.some(u => u.name.trim().toLowerCase() === form.name.trim().toLowerCase());
    if (!userExists) {
      alert("This person does not exist! Please select a valid person. ⚠️");
      return;
    }

    const response = await fetch(
      `${API_BASE}/add_member.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        }, // ✅ YE COMMA IMPORTANT HAI
        body: JSON.stringify(form),
      }
    );
    const result = await response.json();

    if (result.success) {
      fetchMembers();
      setForm({
        name: "",
        email: "",
        role: "Developer",
        dept: "Engineering",
      });
      setOpen(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!editForm.name || !editForm.email) return;

    const response = await fetch(
      `${API_BASE}/update_member.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editForm),
      }
    );
    const result = await response.json();

    if (result.success) {
      fetchMembers();
      setIsEditOpen(false);
    }
  };

  const handleDeleteMember = async (id: string | number) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;

    const response = await fetch(
      `${API_BASE}/delete_member.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      }
    );
    const result = await response.json();

    if (result.success) {
      fetchMembers();
    }
  };

  const openView = (member: Member) => {
    setSelectedMember(member);
    setIsViewOpen(true);
  };

  const openEdit = (member: Member) => {
    setEditForm({
      id: member.id,
      name: member.name,
      email: member.email,
      role: member.role,
      dept: member.dept,
    });
    setIsEditOpen(true);
  };

  return (
    <div className="flex flex-1 bg-gray-50">
      {/* ================= DASHBOARD SIDEBAR ================= */}
      {/* ================= DASHBOARD SIDEBAR ================= */}
      <JiraSidebar activeItem="team" />

      {/* ================= MAIN CONTENT ================= */}
      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Team Management</h1>
            <p className="text-sm text-muted-foreground">
              Manage team members and assignments
            </p>
          </div>

          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setOpen(true)}
          >
            Add Member
          </Button>
        </div>

        {/* Table */}
        <Card className="rounded-2xl">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left">
                  <th className="p-3">Member</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Projects</th>
                  <th>Hourly Rate</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {members.map((m, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-none hover:bg-gray-50"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                          {m.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                        <div>
                          <p className="font-medium">{m.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {m.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>{m.role}</td>
                    <td>{m.dept}</td>
                    <td>{m.projects}</td>
                    <td>{m.rate}</td>
                    <td>
                      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                        {m.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="flex justify-center gap-2">
                        <Eye
                          className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                          onClick={() => openView(m)}
                        />
                        <Pencil
                          className="w-4 h-4 text-blue-500 cursor-pointer hover:text-blue-700"
                          onClick={() => openEdit(m)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      {/* ================= ADD MEMBER MODAL ================= */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <input
              list="user-names"
              placeholder="Full Name"
              className="w-full border px-3 py-2 rounded-md text-sm"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                const user = users.find(u => u.name === name);
                if (user) {
                  setForm({ ...form, name, email: user.email });
                } else {
                  setForm({ ...form, name });
                }
              }}
            />
            <datalist id="user-names">
              {users.map((u, i) => (
                <option key={i} value={u.name} />
              ))}
            </datalist>

            <input
              placeholder="Email"
              className="w-full border px-3 py-2 rounded-md text-sm"
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />

            <select
              className="w-full border px-3 py-2 rounded-md text-sm"
              value={form.role}
              onChange={(e) =>
                setForm({ ...form, role: e.target.value })
              }
            >
              <option>Developer</option>
              <option>Tester</option>
              <option>Designer</option>
              <option>Project Manager</option>
            </select>

            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={handleAddMember}
            >
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ================= VIEW MEMBER MODAL ================= */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>View Team Member</DialogTitle>
          </DialogHeader>

          {selectedMember && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-medium">{selectedMember.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium">{selectedMember.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Role</p>
                  <p className="text-sm font-medium">{selectedMember.role}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Department</p>
                  <p className="text-sm font-medium">{selectedMember.dept}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Projects</p>
                  <p className="text-sm font-medium">
                    {selectedMember.projects}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Hourly Rate</p>
                  <p className="text-sm font-medium">{selectedMember.rate}</p>
                </div>
              </div>
              <Button
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800"
                onClick={() => setIsViewOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ================= EDIT MEMBER MODAL ================= */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500">Full Name</label>
              <input
                placeholder="Full Name"
                className="w-full border px-3 py-2 rounded-md text-sm"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500">Email</label>
              <input
                placeholder="Email"
                className="w-full border px-3 py-2 rounded-md text-sm"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500">Role</label>
              <select
                className="w-full border px-3 py-2 rounded-md text-sm"
                value={editForm.role}
                onChange={(e) =>
                  setEditForm({ ...editForm, role: e.target.value })
                }
              >
                <option>Developer</option>
                <option>Tester</option>
                <option>Designer</option>
                <option>Project Manager</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500">Department</label>
              <select
                className="w-full border px-3 py-2 rounded-md text-sm"
                value={editForm.dept}
                onChange={(e) =>
                  setEditForm({ ...editForm, dept: e.target.value })
                }
              >
                <option>Engineering</option>
                <option>Design</option>
                <option>Marketing</option>
                <option>HR</option>
              </select>
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleUpdateMember}
            >
              Update Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================= SIDEBAR ITEM ================= */


